import { useState } from 'react';

export default function SmsOptOutPage() {
  const [formData, setFormData] = useState({
    phone: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3687jpvp7jurpvbp3q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          phone: '',
          reason: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-message-3-line text-2xl text-white"></i>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">SMS Opt-Out</h1>
              <p className="text-red-100">Unsubscribe from SMS messages</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsubscribe from SMS Messages</h2>
              <p className="text-gray-600 mb-6">
                We're sorry to see you go! If you no longer wish to receive SMS messages from Fast Funding Capital, 
                you can opt out using the form below or by replying "STOP" to any of our text messages.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5">
                    <i className="ri-information-line text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Quick Opt-Out Methods:</h3>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Reply "STOP" to any SMS message from us</li>
                      <li>• Use the form below to unsubscribe online</li>
                      <li>• Call us at 346.291.8220 for immediate removal</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Opt-Out Form */}
            <form id="sms-opt-out-form" data-readdy-form onSubmit={handleSubmit}>
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-check-circle-line text-green-600"></i>
                    </div>
                    <div>
                      <p className="text-green-800 font-medium">Successfully unsubscribed!</p>
                      <p className="text-green-700 text-sm mt-1">
                        Your phone number has been removed from our SMS list. You will no longer receive text messages from Fast Funding Capital.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-error-warning-line text-red-600"></i>
                    </div>
                    <p className="text-red-800 font-medium">There was an error processing your request. Please try again or call us at 346.291.8220.</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number to Unsubscribe *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the phone number you want to remove from our SMS list
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Unsubscribing (Optional)
                  </label>
                  <select
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select a reason (optional)</option>
                    <option value="too-frequent">Messages too frequent</option>
                    <option value="not-relevant">Content not relevant</option>
                    <option value="no-longer-interested">No longer interested in services</option>
                    <option value="wrong-number">Wrong phone number</option>
                    <option value="prefer-email">Prefer email communication</option>
                    <option value="other">Other reason</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </div>
                      Processing Opt-Out...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-close-circle-line"></i>
                      </div>
                      Unsubscribe from SMS
                    </div>
                  )}
                </button>
              </div>
            </form>

            {/* Additional Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Important Information:</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>• Opt-out requests are processed within 24 hours</p>
                <p>• You may receive one final confirmation message</p>
                <p>• This will not affect email communications (manage separately)</p>
                <p>• You can re-subscribe at any time by contacting us</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Need Help?</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Phone: <a href="tel:3462918220" className="text-blue-600 hover:text-blue-800">346.291.8220</a></p>
                <p>Email: <a href="mailto:info@fastfundingcap.com" className="text-blue-600 hover:text-blue-800">info@fastfundingcap.com</a></p>
                <p>Hours: Monday - Friday, 8 AM - 7 PM EST</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}